import { FinancialReport } from '@/repositories/ReportRepository'
import {
  Box,
  Button,
  Card,
  CardBody,
  Icon,
  Select,
  SimpleGrid,
  Text,
} from '@chakra-ui/react'
import { endOfMonth, format, startOfMonth, subDays } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Calendar, XIcon } from 'lucide-react'
import { useMemo } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useNavigate } from 'react-router-dom'

interface ReportFiltersProps {
  startDate: Date
  endDate: Date
  totalDays: number
  payoutData: FinancialReport[]
  selectedProperty: string | undefined
  setSelectedProperty: (property: string | undefined) => void
  onStartDateChange: (date: Date) => void
  onEndDateChange: (date: Date) => void
}

export function ReportFilters({
  startDate,
  endDate,
  totalDays,
  payoutData,
  selectedProperty,
  setSelectedProperty,
  onStartDateChange,
  onEndDateChange,
}: ReportFiltersProps) {
  const yesterday = subDays(new Date(), 1)
  const navigate = useNavigate()

  const properties = useMemo(() => {
    if (payoutData.length === 0) return []
    return payoutData.reduce((acc, report) => {
      return acc.concat(report.properties)
    }, [] as FinancialReport['properties'])
  }, [payoutData])

  return (
    <Card>
      <CardBody>
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              Data Inicial
            </Text>
            <DatePicker
              selected={startDate}
              onChange={(date) =>
                onStartDateChange(date || startOfMonth(new Date()))
              }
              dateFormat="dd/MM/yyyy"
              customInput={
                <Button variant="outline" w="full" justifyContent="flex-start">
                  <Icon as={Calendar} mr={2} />
                  {format(startDate, 'dd/MM/yyyy')}
                </Button>
              }
              locale={ptBR}
            />
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              Data Final
            </Text>
            <DatePicker
              selected={endDate}
              onChange={(date) =>
                onEndDateChange(date || endOfMonth(new Date()))
              }
              dateFormat="dd/MM/yyyy"
              customInput={
                <Button variant="outline" w="full" justifyContent="flex-start">
                  <Icon as={Calendar} mr={2} />
                  {format(endDate, 'dd/MM/yyyy')}
                </Button>
              }
              locale={ptBR}
              maxDate={yesterday}
            />
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              Período
            </Text>
            <Text fontSize="lg" fontWeight="bold" color="blue.600">
              {totalDays} dias
            </Text>
          </Box>

          {selectedProperty === undefined ? (
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Propriedade
              </Text>
              <Select
                placeholder="Selecione uma propriedade"
                value={selectedProperty}
                onChange={(e) => {
                  setSelectedProperty(e.target.value)
                  navigate('/reports')
                }}
              >
                {properties.map((property) => (
                  <option key={property.id} value={property.id}>
                    {property.title}
                  </option>
                ))}
              </Select>
            </Box>
          ) : (
            <Box className="flex flex-col items-center justify-center">
              <Box className="self-start flex gap-2 justify-between w-full">
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Mostrando
                </Text>
                <Button
                  rightIcon={<Icon as={XIcon} />}
                  size="xs"
                  colorScheme="red"
                  aria-label="remove filter"
                  onClick={() => {
                    setSelectedProperty(undefined)
                    navigate('/reports')
                  }}
                >
                  remover seleção
                </Button>
              </Box>
              <Text
                fontSize="sm"
                fontWeight="bold"
                color="blue.600"
                textAlign={'center'}
              >
                {selectedProperty
                  ? properties.find((p) => p.id === selectedProperty)?.title
                  : `${properties.length} propriedades`}
              </Text>
            </Box>
          )}
        </SimpleGrid>
      </CardBody>
    </Card>
  )
}
